import axios from 'axios';
import { getEnvironmentVariable } from '../utils';
import { getAuthTokens } from '../providers/authTokenProvider';

const BASE_URL = getEnvironmentVariable('REACT_APP_API_URL');

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const platformURL = getEnvironmentVariable('REACT_APP_PLATFORM_URL');

const endpointsWithoutToken: string[] = [
    platformURL + '/JWT/react/login',
    platformURL + '/JWT/session/login',
    platformURL + '/JWT/validate',
];

const reactTokenEndpoints: string[] = [
    '/decisions/list/popular',
    '/categories',
    '/decisions/list/trend',
    '/decisions/list/bycategory',
    '/pages/list',
    '/pages/detail',
    '/sitepart/metadata',
];

const isProtectedEndpoint = (endpoint: string | undefined): boolean => {
    if (!endpoint) {
        return false;
    }

    return !endpointsWithoutToken.some(publicURL => endpoint.startsWith(publicURL));
};

const isReactEndpoint = (endpoint: string | undefined): boolean => {
    if (!endpoint) {
        return false;
    }

    return reactTokenEndpoints.some(template => endpoint.startsWith(template));
};

export const getTokenForEndpoint = (endpoint: string | undefined): {
    token: string;
    authHeader: 'AuthorizationUser' | 'AuthorizationReact';
}[] => {
    const tokens = getAuthTokens();
    const tokenInfo: {
        token: string;
        authHeader: 'AuthorizationUser' | 'AuthorizationReact';
    }[] = [ {
        token: tokens.reactToken,
        authHeader: 'AuthorizationReact',
    } ];

    if (isReactEndpoint(endpoint)) {
        return tokenInfo;
    }

    if (tokens.sessionToken) {
        tokenInfo.push({
            token: tokens.sessionToken,
            authHeader: 'AuthorizationUser',
        });
    }

    return tokenInfo;
};

axiosInstance.interceptors.request.use(
    (config) => {
        if (!isProtectedEndpoint(config.url)) {
            return config;
        }

        const tokenInfo = getTokenForEndpoint(config.url);

        tokenInfo.forEach(({ authHeader, token }) => {
            config.headers[authHeader] = token;
        });

        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

axiosInstance.interceptors.request.use((config) => {
    if (config.data instanceof FormData) {
        const headers: any = config.headers ?? {};
        delete headers['Content-Type'];
        delete headers['content-type'];
        delete headers.common?.['Content-Type'];
        delete headers.common?.['content-type'];
        config.headers = headers;

        config.transformRequest = [ (d: any) => d ];
    }
    return config;
});

export default axiosInstance;